import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MILL_SECONDS } from 'common/constant/general';
import { MB_RESPONSE, MB_TYPE, MessageBoxComponent, MessageBoxOptions } from 'components/message-box/message-box.component';
import { SourceSelectionComponent } from 'components/source-selection/source-selection.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { NodeTypes } from "services/definition/class/node-types";
import { DefinitionService } from 'services/definition/definition.service';
import { TelemetryDeliveryMethod, TelemetryType, TreeNodeType } from "services/definition/enum";
import { ISourceNode, ITelemetryNode } from "services/definition/interface";
import { SpinnerTypes } from 'services/loading/spinner/spinner-type';
import { ColorPalette } from 'src/app/common/utility/color-palette/ColorPalette';
import { Logger } from 'src/app/common/utility/logger/logger';
import { ALIGN, ANALYZE_DEFAULT, AnalyzeSettings, FUNC_TYPE, LineSettings, POSITION, TIME_HORIZON, Y_AXIS_POSITION } from '../analyze-common';

// Loading Source List const
const MAX_TIMEOUT = 60;

// Selection ranges
const MAX_FUNC_TIMEFRAME = 999;
const MIN_FUNC_TIMEFRAME = 1;

const MAX_HORIZON = 99;
const MIN_HORIZON = 1;

const LINE_DEFAULT: LineSettings = {
  lineColor: '',
  showSourceName: false,
  showDeviceName: false,
  showUoMSymbol: true,
  showCustomName: false,
  customName: "",
  yAxisPosition: Y_AXIS_POSITION.LEFT,
  minYaxis: null,
  maxYaxis: null,
  showTitle: true,
  function: FUNC_TYPE.AVERAGE,
  functionTimeframe: 5 // 5 Seconds
}


@Component({
  selector: 'analyze-settings',
  templateUrl: './analyze-settings.component.html',
  styleUrls: ['./analyze-settings.component.scss']
})
export class AnalyzeSettingsComponent implements OnInit {
  public get minHorizon(): number { return MIN_HORIZON };
  public get maxHorizon(): number { return MAX_HORIZON };
  public get minFunctionTimeframe(): number { return MIN_FUNC_TIMEFRAME };
  public get maxFunctionTimeframe(): number { return MAX_FUNC_TIMEFRAME };
  public userForm!: FormGroup;
  public fullTree: Array<ISourceNode> = [];

  // Drawer Control settings
  public yAxisFrmCtl = new FormControl("");
  public showTitleFrmCtl = new FormControl();
  public minYaxis = new FormControl();
  public maxYaxis = new FormControl();
  public functionFrmCtl = new FormControl();
  public showSourceNameFrmCtrl = new FormControl();
  public showDeviceNameFrmCtrl = new FormControl();
  public showUoMSymbolFrmCtrl = new FormControl();
  public showCustomNameFrmCtrl = new FormControl();
  public customNameFrmCtrl = new FormControl()

  public functionTimeframeFrmCtl = new FormControl();
  public lineColor: string = '';
  public showSettingsDrawer: boolean = false;

  // Main Dialog
  public titleShow = new FormControl();
  public titleText = new FormControl();
  public titlePosition = new FormControl();
  public titleSize = new FormControl();

  // Used with source-selection
  private showAll: boolean = false;
  private editMode: boolean = true;
  public searchStr: string = '';
  public editingNode!: ITelemetryNode;

  public get getPOSITION(): any {
    return POSITION;
  }

  public get getALIGN(): any {
    return ALIGN;
  }

  public get getYAXISPOSITION(): any {
    return Y_AXIS_POSITION;
  }

  public get TIMEHORIZON(): any {
    return TIME_HORIZON;
  }

  constructor(
    private dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<AnalyzeSettingsComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { settings: AnalyzeSettings }
  ) { }


  public get spinnerType(): string {
    return SpinnerTypes.BallClipRotate;
  }

  public get spinnerName(): string {
    return "sourceList";
  }

  public get isSourcesListReady(): boolean {
    return DefinitionService.SourcesListReady;
  }

  private loadingId = 0;
  private loadingTimeout = MAX_TIMEOUT;
  public ngOnInit() {

    // If Sources List is not ready, then wait
    if (!DefinitionService.SourcesListReady) {
      this.spinner.show(this.spinnerName);

      this.loadingId = window.setInterval(() => {
        // Did we timeout?
        if (this.loadingTimeout && !--this.loadingTimeout) {
          Logger.error("Source Selection List Timed Out")
          // Tell the user we timed out
          const msgBoxSettings: MessageBoxOptions = {
            type: MB_TYPE.YES_NO,
            title: undefined,
            message: "widgets.common.source-list-timeout"
          }
          const msgBox = this.dialog.open(MessageBoxComponent, {
            data: msgBoxSettings
          });
          msgBox.afterClosed().subscribe((res) => {
            if (res.event === MB_RESPONSE.YES) {
              this.loadingTimeout = MAX_TIMEOUT;
            } else {
              this.spinner.hide(this.spinnerName);
              window.clearInterval(this.loadingId)
              this.loadingId = 0;
              // Close dialog
              this.dialogRef.close({
                event: 'cancel',
                data: undefined
              });
            }
          }); // afterClosed subscribe
        } // If loadingTimeout

        // See if the Source List is Ready yet
        if (DefinitionService.SourcesListReady) {
          this.spinner.hide(this.spinnerName)
          window.clearInterval(this.loadingId);
          this.loadingId = 0;
          this.fullTree = DefinitionService.getSourcesList();
        }
      }, MILL_SECONDS)
    } else { // Sit list is Ready
      // Get my sourceNodes
      this.fullTree = DefinitionService.getSourcesList();
    }

    // If data settings have not been initialized
    this.data.settings = this.data.settings ?? ANALYZE_DEFAULT;

    // Setup the userForm for the dialog
    this.userForm = this.formBuilder.group({
      showLegend: this.data.settings.showLegend,
      titleShow: this.data.settings.title.showTitle,
      titleText: this.data.settings.title.text,
      titlePosition: this.data.settings.title.position,
      titleSize: this.data.settings.title.font.size,
    });


    // If nothing has been configured show the tree
    if ((this.data.settings.sourceNodes == undefined) || !this.data.settings.sourceNodes.length) {
      this.showAll = true;
      this.editMode = !this.showAll;
    }
  } // ngOnInit()

  public get sourcesAvailable(): boolean {
    return this.fullTree.length > 0;
  } // sourcesAvailable

  public analyzeSettingsLabels(str: string): string {
    return 'analyze.settings.' + str;
  } // analyzeSettingsLabels()

  // Handle Keyup
  public keyupEvent(event: { key: string; }) {
    // If Escape Key then cancel search
    if (event.key === "Escape") {
      this.searchStr = "";
    }
  } // cancelSearch()

  public getSearchString(): string {
    return this.searchStr;
  } // getSearchString

  // Show/Hide in Tree View
  public toggleShowAll() {
    // When not showing all, allow editMode for selected
    this.showAll = !this.showAll;
    this.editMode = !this.showAll;

    // Make sure defaults are set
    if (this.editMode) {
      this.data.settings.sourceNodes = <Array<any>>SourceSelectionComponent.pruneTree(this.fullTree)
      this.setTelemetryNodeDefaults(this.data.settings.sourceNodes)
    }
  } // toggleShowAll()

  // Returns an array of TelemetryNodes
  private setTelemetryNodeDefaults(nodes: Array<any>) {
    let clr_list = this.setNodeDefaults(nodes);
    clr_list = ColorPalette.getColors(clr_list);
    this.setNodeColors(nodes, clr_list);
  } // setTelemetryNodeDefaults()

  private setNodeColors(nodes: any, clr: Array<string>, ndx: number = 0): number {
    // for each node set the default color
    nodes.forEach((node: any) => {
      if (node.children) {
        ndx = this.setNodeColors(node.children, clr, ndx);
      } else { // Telemetry Node
        // Check for array issues first
        if ((ndx == undefined) || (ndx > clr.length)) {
          Logger.error("ndx exceeded bounds")
        } else {
          node.settings.lineColor = clr[ndx++];
        }
      } // if/else
    });
    return ndx;
  } // setNodeColors()

  // Set Telemetry Line Defaults and returns list of colors for each node
  private colorsList: Array<string> = [];
  private setNodeDefaults(nodes: Array<any>): Array<string> {
    // Check for new Telemetry Nodes, default nodes
    nodes.forEach((node) => {
      // If Children, then not TelemetryNode
      if (NodeTypes.getNodeType(node) !== TreeNodeType.Telemetry) {
        this.setNodeDefaults(node.children)
      } else { // Telemetry Node
        if (node.settings == undefined) {
          node.settings = structuredClone(LINE_DEFAULT);
          node.settings.maxYaxis = node.ltmiRef?.defaults?.maxYaxis != undefined ? Number(node.ltmiRef.defaults.maxYaxis) : null
          node.settings.minYaxis = node.ltmiRef?.defaults?.minYaxis != undefined ? Number(node.ltmiRef.defaults.minYaxis) : null
      }
        this.colorsList.push(node.settings.lineColor)
      }
    });

    return this.colorsList;
  } // setNodeDefaults()

  // Called when the user selects edit option in tree
  public get getTelemetryNodeName(): string {
    return this.editingNode != undefined ? this.editingNode.displayName : "Unknown"
  } // getTelemetryNodeName()

  public changeSettingsRequest() {
    // TODO Just to be safe temp until editMode init done, prevent errors
    if (this.editingNode.settings == undefined) {
      this.editingNode.settings = structuredClone(LINE_DEFAULT);
    }

    this.lineColor = this.editingNode.settings.lineColor;
    this.functionFrmCtl.setValue(this.editingNode.settings.function);
    this.functionTimeframeFrmCtl.setValue(this.editingNode.settings.functionTimeframe)
    this.showTitleFrmCtl.setValue(this.editingNode.settings.showTitle);
    this.yAxisFrmCtl.setValue(this.editingNode.settings.yAxisPosition);
    this.minYaxis.setValue(this.editingNode.settings.minYaxis);
    this.maxYaxis.setValue(this.editingNode.settings.maxYaxis);
    this.showSourceNameFrmCtrl.setValue(this.editingNode.settings.showSourceName);
    this.showDeviceNameFrmCtrl.setValue(this.editingNode.settings.showDeviceName);
    this.showUoMSymbolFrmCtrl.setValue(this.editingNode.settings.showUoMSymbol);
    this.showCustomNameFrmCtrl.setValue(this.editingNode.settings.showCustomName);
    this.customNameFrmCtrl.setValue(this.editingNode.settings.customName);

    this.showSettingsDrawer = true;
  } // changeSettings()

  public applyDrawerDisabled(): boolean {
    if ((this.functionTimeframeFrmCtl.value < this.minFunctionTimeframe)
      || (this.functionTimeframeFrmCtl.value > this.maxFunctionTimeframe)) {
      return true;
    }
    return false;
  } // applyDrawerDisabled()

  public applyDrawer() {
    this.showSettingsDrawer = false;

    // Save the settings
    this.editingNode.settings.lineColor = this.lineColor;
    this.editingNode.settings.function = this.functionFrmCtl.value;
    this.editingNode.settings.functionTimeframe = this.functionTimeframeFrmCtl.value;
    this.editingNode.settings.yAxisPosition = this.yAxisFrmCtl.value;
    this.editingNode.settings.showTitle = this.showTitleFrmCtl.value;
    this.editingNode.settings.minYaxis = this.minYaxis.value;
    this.editingNode.settings.maxYaxis = this.maxYaxis.value;
    this.editingNode.settings.showSourceName = this.showSourceNameFrmCtrl.value;
    this.editingNode.settings.showDeviceName = this.showDeviceNameFrmCtrl.value;
    this.editingNode.settings.showUoMSymbol = this.showUoMSymbolFrmCtrl.value;
    this.editingNode.settings.showCustomName = this.showCustomNameFrmCtrl.value;
    this.editingNode.settings.customName = this.customNameFrmCtrl.value;

  } // applyDrawer()

  public cancelDrawer() {
    this.showSettingsDrawer = false;
  } // cancelDrawer()

  public drawerClosed() {
    this.showSettingsDrawer = false;
  }

  public getShowAll(): boolean {
    return this.showAll
  } // getShowAll()

  public typeFilter(): Array<TelemetryType> | undefined {
    return [TelemetryType.Telemetry]
  }

  public deliveryFilter(): Array<TelemetryDeliveryMethod> | undefined {
    return [TelemetryDeliveryMethod.Streaming]
  }

  public getEditMode(): boolean {
    return this.editMode;
  } // getEditMode()

  // Used by Dashboard when it creates a Line Widget
  public static getDefault(): AnalyzeSettings {
    return ANALYZE_DEFAULT;
  } // getDefault()

  // Enable/Disable showTitle FormControl
  public changeYAxis(event: any) {
    (event.value == Y_AXIS_POSITION.NONE) ? this.showTitleFrmCtl.disable() : this.showTitleFrmCtl.enable();
  } // changeYAxis()

  public applySettingsDisabled(): boolean {
    if ((this.userForm.get('timeHorizonDuration')?.getRawValue() < this.minHorizon)
      || (this.userForm.get('timeHorizonDuration')?.getRawValue() > this.maxHorizon)
      || !DefinitionService.SourcesListReady) {
      return true;
    }
    return false;
  } // applySettingsDisabled

  // Apply settings selected, update settings
  public apply() {
    this.data.settings.sourceNodes = <Array<any>>SourceSelectionComponent.pruneTree(this.fullTree)
    this.setTelemetryNodeDefaults(this.data.settings.sourceNodes);

    // Save the general settings from Form
    this.data.settings.showLegend = this.userForm.get('showLegend')?.getRawValue();
    this.data.settings.title.showTitle = this.userForm.get('titleShow')?.getRawValue();
    this.data.settings.title.text = this.userForm.get('titleText')?.getRawValue();
    this.data.settings.title.position = this.userForm.get('titlePosition')?.getRawValue();
    this.data.settings.title.font.size = this.userForm.get('titleSize')?.getRawValue();

    // Apply selected, return selected data
    this.dialogRef.close({
      event: 'apply',
      data: this.data
    });
  } // apply()

  // Cancel selected, discard settings
  public cancel() {
    // If we are loading, then cleanup
    if (this.loadingId) {
      this.spinner.hide(this.spinnerName);
      window.clearInterval(this.loadingId)
      this.loadingId = 0;
    }
    // Issue Close
    this.dialogRef.close({
      event: 'cancel',
      data: undefined
    });
  } // cancel()

} // class AnalyzeSettingsComponent
